import { useEffect, useState } from 'react';
import {
  canManageFamily,
  type FamilyInviteView,
  type FamilyMembershipView,
  type FamilyMemberView,
  type FamilyRole,
  type PendingInviteView,
} from '@kidzim/shared';
import { Avatar, Badge, Button, Card, ErrorState, Input } from '../design-system/components';
import { api, type ApiError } from '../lib/api';

const ROLE_LABEL: Record<FamilyRole, string> = {
  owner: 'Owner',
  co_parent: 'Co-parent',
  trusted_adult: 'Trusted adult',
};

interface FamilyResponse {
  family: { id: string; name: string; deletionScheduledAt: string | null; purgeAt: string | null };
  me: { role: FamilyRole };
  members: FamilyMemberView[];
  invites: FamilyInviteView[];
  families: FamilyMembershipView[];
}

export function GrownUps() {
  const [data, setData] = useState<FamilyResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pendingInvites, setPendingInvites] = useState<PendingInviteView[]>([]);

  async function refresh() {
    setError(null);
    try {
      const res = await api<FamilyResponse>('/api/family');
      setData(res);
    } catch (err) {
      setData(null);
      setError(err as ApiError);
    }
  }
  async function refreshPendingInvites() {
    const res = await api<{ invites: PendingInviteView[] }>('/api/family/invites/pending');
    setPendingInvites(res.invites);
  }
  useEffect(() => {
    refresh();
    refreshPendingInvites().catch(() => undefined);
  }, []);

  async function respondToInvite(id: string, action: 'accept' | 'decline') {
    await api(`/api/family/invites/${id}/${action}`, { method: 'POST' });
    await Promise.all([refresh(), refreshPendingInvites()]);
  }

  if (error) {
    if (error.error === 'forbidden') {
      return <ErrorState message="This page is for parents managing a family. As an admin, you don't have a family here — use the Admin console instead." />;
    }
    if (error.error === 'no_family') {
      return (
        <div className="kz-stack" style={{ gap: 'var(--space-6)', maxWidth: 640 }}>
          <ErrorState message="You don't currently belong to a family. Ask another grown-up in your household for an invite code, or accept a pending invite below." />
          <PendingInvites invites={pendingInvites} onRespond={respondToInvite} />
        </div>
      );
    }
    return <ErrorState message="Couldn't load the grown-ups in your family." onRetry={refresh} />;
  }
  if (!data) return <p className="kz-muted">Loading…</p>;
  const canManage = canManageFamily(data.me.role);
  const isOwner = data.me.role === 'owner';

  async function switchFamily(familyId: string) {
    await api('/api/family/switch', { method: 'POST', body: JSON.stringify({ familyId }) });
    refresh();
  }
  async function leaveFamily() {
    if (!data) return;
    if (!confirm(`Leave the "${data.family.name}" family? You'll lose access to its kids and rooms.`)) return;
    await api('/api/family/leave', { method: 'POST', body: JSON.stringify({ familyId: data.family.id }) });
    refresh();
  }
  async function transferOwnership(toUserId: string, toName: string) {
    if (!confirm(`Make ${toName} the owner of "${data!.family.name}"? You'll become a co-parent.`)) return;
    await api('/api/family/transfer-ownership', { method: 'POST', body: JSON.stringify({ toUserId }) });
    refresh();
  }

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)', maxWidth: 640 }}>
      <div>
        <span className="kz-eyebrow">Grown-ups</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>The adults in your family</h1>
        <p className="kz-muted">
          Add a co-parent who can manage everything, or a trusted adult (a grandparent, a sitter) who can chat with the
          kids in rooms you add them to. Share the invite code, or send it to their email.
        </p>
      </div>

      {data.family.deletionScheduledAt && (
        <Card style={{ background: 'var(--flag-100)', borderColor: 'var(--flag-500)' }}>
          <h3 style={{ color: 'var(--flag-500)' }}>This family is scheduled for deletion</h3>
          <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
            "{data.family.name}" — its kids, rooms, and transcripts — will be permanently removed on{' '}
            {new Date(data.family.purgeAt!).toLocaleDateString()}.
            {isOwner ? ' You can cancel any time before then.' : ' Ask the owner to cancel if this is unexpected.'}
          </p>
          {isOwner && (
            <Button
              variant="secondary"
              size="sm"
              style={{ marginTop: 'var(--space-3)' }}
              onClick={async () => {
                await api('/api/family/delete/cancel', { method: 'POST' });
                refresh();
              }}
            >
              Cancel deletion
            </Button>
          )}
        </Card>
      )}

      <PendingInvites invites={pendingInvites} onRespond={respondToInvite} />

      {data.families.length > 1 && (
        <section>
          <h3 style={{ marginBottom: 'var(--space-3)' }}>Your families</h3>
          <Card className="kz-stack" style={{ gap: 'var(--space-2)' }}>
            {data.families.map((f) => (
              <div key={f.familyId} className="kz-row" style={{ justifyContent: 'space-between', padding: '6px 0' }}>
                <span>
                  <strong>{f.name}</strong> <span className="kz-muted">· {ROLE_LABEL[f.role]}</span>
                </span>
                {f.isActive ? (
                  <Badge tone="safe">Active</Badge>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => switchFamily(f.familyId)}>Switch to</Button>
                )}
              </div>
            ))}
          </Card>
        </section>
      )}

      <section>
        <h3 style={{ marginBottom: 'var(--space-3)' }}>Members</h3>
        <Card className="kz-stack" style={{ gap: 'var(--space-2)' }}>
          {data.members.map((mem) => (
            <div key={mem.userId} className="kz-row" style={{ justifyContent: 'space-between', padding: '6px 0' }}>
              <div className="kz-row" style={{ gap: 'var(--space-3)' }}>
                <Avatar name={mem.displayName} />
                <div>
                  <p style={{ font: 'var(--type-body-strong)' }}>
                    {mem.displayName} {mem.isYou && <span className="kz-muted">(you)</span>}
                  </p>
                  <p className="kz-muted" style={{ font: 'var(--type-small)' }}>{ROLE_LABEL[mem.role]}</p>
                </div>
              </div>
              <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                {isOwner && !mem.isYou && (
                  <Button variant="ghost" size="sm" onClick={() => transferOwnership(mem.userId, mem.displayName)}>
                    Make owner
                  </Button>
                )}
                {canManage && !mem.isYou && mem.role !== 'owner' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!confirm(`Remove ${mem.displayName} from your family?`)) return;
                      await api(`/api/family/members/${mem.userId}`, { method: 'DELETE' });
                      refresh();
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
      </section>

      {canManage ? (
        <InviteAdult invites={data.invites} onChange={refresh} />
      ) : (
        <Card style={{ background: 'var(--blue-50)', borderColor: 'var(--blue-100)' }}>
          <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
            You're a trusted adult here. A co-parent or the owner manages kids, settings, and invites.
          </p>
        </Card>
      )}

      {data.me.role !== 'owner' && (
        <section>
          <h3 style={{ marginBottom: 'var(--space-2)' }}>Leave this family</h3>
          <Card className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
              Stop being a grown-up in “{data.family.name}”. You'll lose access to its kids, rooms, and chats.
            </p>
            <Button variant="danger" size="sm" onClick={leaveFamily}>Leave family</Button>
          </Card>
        </section>
      )}

      {isOwner && !data.family.deletionScheduledAt && <DeleteFamilySection familyName={data.family.name} onScheduled={refresh} />}
    </div>
  );
}

function PendingInvites({
  invites,
  onRespond,
}: {
  invites: PendingInviteView[];
  onRespond: (id: string, action: 'accept' | 'decline') => Promise<void>;
}) {
  if (invites.length === 0) return null;
  return (
    <section>
      <h3 style={{ marginBottom: 'var(--space-3)' }}>Invitations waiting for you</h3>
      <Card className="kz-stack" style={{ gap: 'var(--space-2)' }}>
        {invites.map((inv) => (
          <div key={inv.id} className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
            <span>
              <strong>{inv.familyName}</strong>{' '}
              <span className="kz-muted">invited you as a {ROLE_LABEL[inv.role].toLowerCase()}</span>
            </span>
            <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
              <Button variant="secondary" size="sm" onClick={() => onRespond(inv.id, 'decline')}>Decline</Button>
              <Button size="sm" onClick={() => onRespond(inv.id, 'accept')}>Accept</Button>
            </div>
          </div>
        ))}
      </Card>
    </section>
  );
}

function DeleteFamilySection({ familyName, onScheduled }: { familyName: string; onScheduled: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <section>
      <h3 style={{ color: 'var(--flag-500)', marginBottom: 'var(--space-2)' }}>Delete this family</h3>
      <Card style={{ borderColor: 'var(--flag-500)' }}>
        <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
          This schedules "{familyName}" — its kids, rooms, transcripts, and every grown-up's membership — for
          permanent deletion in 30 days. You can cancel any time before then.
        </p>
        {!confirming ? (
          <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>Delete this family</Button>
        ) : (
          <form
            className="kz-stack"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              try {
                await api('/api/family/delete', { method: 'POST', body: JSON.stringify({ currentPassword: password }) });
                await onScheduled();
              } catch (err) {
                const apiErr = err as ApiError;
                setError(apiErr.error === 'invalid_password' ? 'That password is incorrect.' : 'Something went wrong. Try again.');
              }
            }}
          >
            <Input label="Confirm with your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            {error && <p className="kz-error-text">{error}</p>}
            <div className="kz-row">
              <Button type="submit" variant="danger" size="sm">Schedule deletion</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)}>Keep this family</Button>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}

function InviteAdult({ invites, onChange }: { invites: FamilyInviteView[]; onChange: () => void }) {
  const [role, setRole] = useState<'co_parent' | 'trusted_adult'>('trusted_adult');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function createInvite() {
    setBusy(true);
    setCode(null);
    try {
      const trimmed = email.trim();
      const body = trimmed ? { role, email: trimmed } : { role };
      const res = await api<{ code: string }>('/api/family/invites', { method: 'POST', body: JSON.stringify(body) });
      setCode(res.code);
      setEmail('');
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3 style={{ marginBottom: 'var(--space-3)' }}>Invite a grown-up</h3>
      <Card className="kz-stack" style={{ gap: 'var(--space-4)' }}>
        <label className="kz-stack" style={{ gap: 'var(--space-1)' }}>
          <span style={{ font: 'var(--type-label)' }}>Their role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'co_parent' | 'trusted_adult')}
            style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border-default)', font: 'var(--type-body)' }}
          >
            <option value="trusted_adult">Trusted adult — can chat in rooms you add them to</option>
            <option value="co_parent">Co-parent — full management, like you</option>
          </select>
        </label>
        <label className="kz-stack" style={{ gap: 'var(--space-1)' }}>
          <span style={{ font: 'var(--type-label)' }}>Their email <span className="kz-muted">(optional — we'll email the invite)</span></span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="grandma@example.com"
            style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border-default)', font: 'var(--type-body)' }}
          />
        </label>
        <Button onClick={createInvite} disabled={busy}>{busy ? 'One sec…' : 'Create invite code'}</Button>

        {code && (
          <div style={{ background: 'var(--blue-50)', border: 'var(--border-width) solid var(--blue-100)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)' }}>
            <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-2)' }}>
              Share this code with them. It works once and expires — we won't show it again.
            </p>
            <div className="kz-row" style={{ justifyContent: 'space-between', gap: 'var(--space-2)' }}>
              <code className="kz-mono" style={{ fontSize: 'var(--text-xl)', letterSpacing: 2, color: 'var(--ink-900)' }}>{code}</code>
              <Button variant="secondary" size="sm" onClick={() => navigator.clipboard?.writeText(code)}>Copy</Button>
            </div>
            <p className="kz-muted" style={{ font: 'var(--type-small)', marginTop: 'var(--space-2)' }}>
              They enter it in the “Family invite code” box when creating their grown-up account.
            </p>
          </div>
        )}

        {invites.length > 0 && (
          <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
            <p style={{ font: 'var(--type-label)' }}>Pending invites</p>
            {invites.map((inv) => (
              <div key={inv.id} className="kz-row" style={{ justifyContent: 'space-between', padding: '4px 0' }}>
                <Badge tone="neutral">{ROLE_LABEL[inv.role]}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await api(`/api/family/invites/${inv.id}`, { method: 'DELETE' });
                    onChange();
                  }}
                >
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}
